/**
 * SfxEventMap: Pure mapping module for audio event keys.
 * No Babylon imports — used to centralize string constants for SFX routing.
 */

/**
 * Maps an NPC species to its calm/purr SFX key.
 * @param species NPC type (kitten, crow, squirrel, opossum, fox, etc.)
 * @returns SFX key string
 */
export function speciesSfxForCalm(species: string): string {
  const mapping: Record<string, string> = {
    kitten: "kitten-mew",
    crow: "crow-caw",
    squirrel: "squirrel-chitter",
    opossum: "opossum-squeak",
    fox: "fox-growl",
  };
  return mapping[species] ?? "purr";
}

/**
 * Routes Elder types to their chime SFX key.
 * Gus gets a custom chime; all others use the generic elder chime.
 * @param elder Elder name (e.g., "gus", "leo", undefined)
 * @returns SFX key string
 */
export function elderChimeKey(elder?: string): string {
  if (elder === "gus") {
    return "elder-gus-chime";
  }
  return "elder-chime";
}

/**
 * Returns the blocked SFX key for any ability block reason.
 * Currently always "rhythm-miss" regardless of reason.
 * @param reason Block reason (cooldown, energy, range, locked, grounded)
 * @returns SFX key string
 */
export function abilityBlockedSfxKey(reason: string): string {
  return "rhythm-miss";
}

/**
 * Returns the toast message for an ability block, or null if silent.
 * Toasts only on range, energy, and locked reasons.
 * @param reason Block reason
 * @param partnerName Optional name for partner-related messages
 * @returns Toast copy string or null
 */
export function blockedToastFor(reason: string, partnerName?: string): string | null {
  switch (reason) {
    case "range":
      return `Get closer to ${partnerName ?? "your partner"}`;
    case "energy":
      return "Not enough energy";
    case "locked":
      return "Not unlocked yet";
    case "cooldown":
    case "grounded":
      return null;
    default:
      return null;
  }
}
